import * as authActions from '../actions/auth.actions';
import * as fromCore from '../reducers/index';

import { Action, Store, select } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  AuthService,
  LoginApiError,
  LoginResponse,
} from '../services/auth.service';
import { EMPTY, Observable, of } from 'rxjs';
import { RecentSearch, User } from '../models/core.models';
import {
  catchError,
  concatMap,
  first,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  changePassword,
  changePasswordSuccess,
  listCompanyObjects,
  listCompanyObjectsSuccess,
} from '../actions/auth.actions';
import {
  setCoreLanguage,
  setCoreLanguageSuccess,
} from '../actions/core.actions';

import { Injectable } from '@angular/core';
import { LoggerService } from '@services/logger.service';
import { Router } from '@angular/router';
import { TabSessionService } from '../services/tab-session.service';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../services/user.service';

type LoginSuccessAction = ReturnType<typeof authActions.loginSuccess>;
@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private userService: UserService,
    private toastService: ToastService,
    private router: Router,
    private store: Store,
    private logger: LoggerService,
    private translateService: TranslateService,
    private tabSessionService: TabSessionService,
  ) {}

  login$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.login),
      switchMap((action) =>
        this.authService.login(action.credentials).pipe(
          map((response: LoginResponse) => {
            return authActions.loginSuccess({
              response,
              remember: action.credentials.remember_me || false,
            });
          }),
          catchError((error: LoginApiError) =>
            of(authActions.loginFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.loginSuccess, authActions.remoteLoginSuccess),
      withLatestFrom(this.store.select(fromCore.selectRedirectUrl)), // get redirectUrl from store
      switchMap(
        ([action, redirectUrl]: [LoginSuccessAction, string | undefined]) => {
          this.authService.setCredentials(
            {
              token: action.response.access_token,
              expiresAt: Number(action.response.access_token_expires_at) || undefined,
            },
            action.remember,
          );

          // Initialize per-tab company context from the user's active company
          const activeCompanyId = action.response.user?.active_company_id;
          if (activeCompanyId && !this.tabSessionService.getActiveCompanyId()) {
            this.tabSessionService.setActiveCompanyId(activeCompanyId);
          }

          // If there's a redirectUrl, navigate to it
          if (redirectUrl) {
            this.logger.debug('Redirecting to', redirectUrl);
            this.router.navigateByUrl(redirectUrl);
            // Dispatch two actions: one to load alerts count and one to load notifications count
          }
          return of(
            authActions.loadAlertsCount(),
            authActions.loadNotificationsCount(),
          );
        },
      ),
    ),
  );

  logout$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.logout),
      tap(() => this.userService.clearCaches()), // <-- clear User service caches regardless of the success of the logout
      switchMap(() =>
        this.authService.logout().pipe(
          map(() => {
            return authActions.logoutSuccess();
          }),
          catchError((error) => of(authActions.logoutFailure({ error }))),
        ),
      ),
    ),
  );

  authCheck$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.authCheck),
        // NOTE: no take(1) here — it completed the whole effect after the first
        // authCheck, so later dispatches were silently ignored
        withLatestFrom(this.store.pipe(select(fromCore.selectIsAuthenticated))),
        tap(([action, isAuthenticated]) => {
          const onAuthRoute =
            this.router.url.startsWith('/auth') ||
            window.location.pathname.startsWith('/auth');
          if (!isAuthenticated && !onAuthRoute) {
            // Navigate only if the user is not authenticated and not already on a public auth page
            // (login, forgot-password, create-password/:token, reset-password/:token, validate-account, remote/:authToken)
            this.router
              .navigate(['/auth/login'])
              .then(() => {
                // Handle successful navigation if needed
              })
              .catch((error) => {
                // Handle navigation error
                console.error('Navigation error in authCheck$ effect', error);
              });
          }
        }),
        catchError((error) => {
          // Handle the error from the entire observable chain
          console.error('Error in authCheck$ effect', error);
          return of({ type: '[Auth] Auth Check Error', error });
        }),
      ),
    { dispatch: false },
  );

  alertCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.loadAlertsCount),
      withLatestFrom(this.store.pipe(select(fromCore.selectUser))),
      switchMap(([action, user]) =>
        this.userService.fetchAlertsCount((user as User).id).pipe(
          map((count) => authActions.loadAlertsCountSuccess({ count })),
          catchError((error) =>
            of(authActions.loadAlertsCountFailure({ error })),
          ),
        ),
      ),
    ),
  );

  notificationCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.loadNotificationsCount),
      withLatestFrom(this.store.pipe(select(fromCore.selectUser))),
      switchMap(([action, user]) =>
        this.userService.fetchNotificationsCount((user as User).id).pipe(
          map((count) => authActions.loadNotificationsCountSuccess({ count })),
          catchError((error) =>
            of(authActions.loadNotificationsCountFailure({ error })),
          ),
        ),
      ),
    ),
  );

  triggeredAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.loadTriggeredAlerts),
      withLatestFrom(this.store.pipe(select(fromCore.selectUser))),
      switchMap(([action, user]) =>
        this.userService.fetchTriggeredAlerts((user as User).id).pipe(
          map((alerts) => authActions.loadTriggeredAlertsSuccess({ alerts })),
          catchError((error) =>
            of(authActions.loadTriggeredAlertsFailure({ error })),
          ),
        ),
      ),
    ),
  );

  latestNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.loadLatestNotifications),
      withLatestFrom(this.store.pipe(select(fromCore.selectUser))),
      switchMap(([action, user]) =>
        this.userService.fetchLatestNotifications((user as User).id).pipe(
          map((notifications) =>
            authActions.loadLatestNotificationsSuccess({ notifications }),
          ),
          catchError((error) =>
            of(authActions.loadLatestNotificationsFailure({ error })),
          ),
        ),
      ),
    ),
  );

  fetchActivatedUserCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.fetchActivatedUserCompanies),

      switchMap((action) =>
        this.userService
          .fetchActivatedUserCompanies(action.query, action.start)
          .pipe(
            map((response) =>
              authActions.fetchActivatedUserCompaniesSuccess({
                companies: response.rowData,
                total: response.rowCount,
              }),
            ),
            catchError((error) => EMPTY),
          ),
      ),
    ),
  );

  switchUserCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.switchUserCompany),
      withLatestFrom(this.store.pipe(select(fromCore.selectUser))),
      switchMap(([action, user]) =>
        this.userService.switchCompany(action.companyId).pipe(
          tap(() => this.tabSessionService.setActiveCompanyId(action.companyId)),
          map((user) => authActions.switchUserCompanySuccess({ user })),
          catchError((error) => EMPTY),
        ),
      ),
    ),
  );

  switchUserCompanySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.switchUserCompanySuccess),
        tap(({ user }) => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'msg.company-switch.company-switched-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  switchUserLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setCoreLanguage),
      switchMap((action) =>
        this.userService.setUserLanguage(action.language.id).pipe(
          map(() => setCoreLanguageSuccess()),
          catchError((error) => EMPTY),
        ),
      ),
    ),
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.changePassword),
      switchMap((action) =>
        this.userService
          .changePassword(
            action.userId,
            action.currentPassword,
            action.newPassword,
            action.confirmPassword,
          )
          .pipe(
            map(() => authActions.changePasswordSuccess()),
            catchError((error) =>
              of(authActions.changePasswordFail({ error })),
            ),
          ),
      ),
    ),
  );

  // Non-dispatching side-effects
  changePasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.changePasswordSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant('msg.password.change.success'),
          );
        }),
      ),
    { dispatch: false },
  );

  changePasswordFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.changePasswordFail),
        tap(() => {
          this.toastService.showError(
            this.translateService.instant('msg.password.change.failed'),
          );
        }),
      ),
    { dispatch: false },
  );

  importUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.importUsers),
      switchMap((action) =>
        this.userService
          .importUsers(action.companyId, action.file, action.invite)
          .pipe(
            map(() =>
              authActions.importUsersSuccess({
                companyId: action.companyId,
                file: action.file,
                invite: action.invite,
              }),
            ),
            catchError((error) => of(authActions.importUsersFail({ error }))),
          ),
      ),
    ),
  );

  importUsersSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.importUsersSuccess),
      tap(() => {
        this.toastService.showSuccess(
          this.translateService.instant(
            'admin-module.msg.users-imported-successfully',
          ),
        );
      }),
    ),
  );

  importUsersFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.importUsersFail),
      tap(() => {
        this.toastService.showError(
          this.translateService.instant('admin-module.msg.users-import-failed'),
        );
      }),
    ),
  );

  remoteLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.remoteLogin),
      switchMap(({ token }) =>
        this.authService.loginToken(token).pipe(
          map((response) => {
            if ((response as any)?.error) {
              return authActions.remoteLoginFail({
                error: (response as any).message || 'Login failed',
              });
            }

            return authActions.remoteLoginSuccess({
              response: response as LoginResponse,
            });
          }),
          catchError((err) =>
            of(
              authActions.remoteLoginFail({
                error:
                  err?.error?.message || err?.error?.error || 'Login failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  remoteLoginFailUi$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.remoteLoginFail),
        tap(({ error }) => {
          this.toastService.showError(error || 'Login failed');
          this.router.navigate(['/auth/login']);
        }),
      ),
    { dispatch: false },
  );

  listCompanyObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.listCompanyObjects),
      switchMap((action) =>
        this.userService.listCompanyObjects(action.listCompanyObjects).pipe(
          map((response) =>
            authActions.listCompanyObjectsSuccess({
              listCompanyObjects: response.list_company_objects,
            }),
          ),
          catchError((error) =>
            of(authActions.listCompanyObjectsFail({ error })),
          ),
        ),
      ),
    ),
  );

  /* Load recent searches */
  loadRecentSearches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.loadRecentSearches),
      switchMap(() =>
        this.userService.fetchRecentSearches().pipe(
          map((searches) =>
            authActions.loadRecentSearchesSuccess({ searches }),
          ),
          catchError((err) =>
            of(authActions.loadRecentSearchesFailure({ error: err })),
          ),
        ),
      ),
    ),
  );

  /* Add recent search
     Backend returns { success: true }, so we optimistically build the RecentSearch
     with a client-side time_updated. If your API returns the saved row, map that instead. */
  addRecentSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.addRecentSearch),
      concatMap(({ search }) =>
        this.userService.addRecentSearch(search).pipe(
          map(() => {
            const optimistic: RecentSearch = {
              query_text: search.query_text,
              filters: search.filters ?? null,
              lang: search.lang ?? null,
              scope_id: search.scope_id ?? null,
              result_count: search.result_count ?? null,
              time_updated: Math.floor(Date.now() / 1000),
            };
            return authActions.addRecentSearchSuccess({ search: optimistic });
          }),
          catchError((err) =>
            of(authActions.addRecentSearchFailure({ error: err })),
          ),
        ),
      ),
    ),
  );

  // effect
  removeRecentSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.removeRecentSearch),
      concatMap(({ byFields }) => {
        const req$ = this.userService.removeRecentSearchByFields(byFields!);
        return req$.pipe(
          map(() => authActions.removeRecentSearchSuccess({ byFields })),
          catchError((err) =>
            of(authActions.addRecentSearchFailure({ error: err })),
          ),
        );
      }),
    ),
  );
}
